import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { formatInTimezone, useTimezone } from '@kioskgaming/ui';
import {
  formatWithdrawalPayoutRefundSource,
  type WithdrawalPayoutRefundDisplay,
} from '../../../utils/withdrawalPayoutRefundDisplay';
import { formatCashAmount } from '../../../utils/transactionDisplayUtils';

interface WithdrawalPayoutRefundBannerProps {
  refund: WithdrawalPayoutRefundDisplay;
  currency?: string;
}

export default function WithdrawalPayoutRefundBanner({
  refund,
  currency = 'USD',
}: WithdrawalPayoutRefundBannerProps) {
  const { timezone } = useTimezone();
  const refundedAtLabel = formatInTimezone(refund.refundedAt, timezone);
  const sourceLabel = formatWithdrawalPayoutRefundSource(refund.source);

  return (
    <div
      role="status"
      data-testid="withdrawal-payout-refund-banner"
      className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-950 flex gap-2"
    >
      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 mt-0.5" />
      <div className="min-w-0 space-y-1">
        <p className="font-semibold text-emerald-900">Withdrawal failed — player wallet refunded</p>
        <p className="leading-snug">
          {formatCashAmount(refund.refundAmount, currency)} was returned to the player&apos;s wallet.
          No funds were sent externally.
        </p>
        <dl className="grid gap-1 sm:grid-cols-2 text-[11px] text-emerald-900/90">
          <div>
            <dt className="text-emerald-800/80">Refunded at</dt>
            <dd className="font-medium">{refundedAtLabel || refund.refundedAt}</dd>
          </div>
          <div>
            <dt className="text-emerald-800/80">Wallet credit ID</dt>
            <dd className="font-mono break-all">{refund.refundTransactionId}</dd>
          </div>
          {refund.walletTransactionId != null ? (
            <div>
              <dt className="text-emerald-800/80">Wallet ledger row</dt>
              <dd className="font-mono">#{refund.walletTransactionId}</dd>
            </div>
          ) : null}
          {sourceLabel ? (
            <div>
              <dt className="text-emerald-800/80">Refund source</dt>
              <dd className="capitalize">{sourceLabel}</dd>
            </div>
          ) : null}
        </dl>
      </div>
    </div>
  );
}
